import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { Layout } from '~/eds/src/components/Layout/Layout';
import { Text } from '~/eds/src/components/Text/Text';
import { Info } from '~/eds/src/modules/Info/Info';

interface Props {
  isExactTextMatch: boolean;
  onToggleIsExactTextMatch: (updatedIsExactTextMatch: boolean) => void;
}

export const EvaluateSettings = ({
  isExactTextMatch,
  onToggleIsExactTextMatch,
}: Props) => {
  const intl = useIntl();
  const [isExactTextMatchEnabled, setIsExactTextMatchEnabled] =
    useState(isExactTextMatch);

  const handleToggleIsExactTextMatch = (updatedIsExactTextMatch: boolean) => {
    setIsExactTextMatchEnabled(updatedIsExactTextMatch);
    onToggleIsExactTextMatch(updatedIsExactTextMatch);
  };

  // Keep state in sync with prop changes
  useEffect(() => {
    setIsExactTextMatchEnabled(isExactTextMatch);
  }, [isExactTextMatch]);

  const sections = [
    {
      title: '',
      infos: [
        {
          title: intl.formatMessage({
            id: 'EvaluateSettings.answersMatchExactly',
            defaultMessage: 'Answers match exactly',
            description: 'Title for exact text match setting',
          }),
          description: intl.formatMessage({
            id: 'EvaluateSettings.answersMatchExactlyDescription',
            defaultMessage:
              'Turning this on will cause the AI to only grade outputs as Correct if it is exactly the same as the Correct Answer you entered. By default, it checks if the meaning is the same as the Correct Answer.',
            description:
              'Description for exact text match setting functionality',
          }),
          type: 'content' as const,
          props: {
            children: null,
          },
          toggle: {
            enabled: isExactTextMatchEnabled,
            name: intl.formatMessage({
              id: 'EvaluateSettings.answersMatchExactlyToggle',
              defaultMessage: 'answers match exactly',
              description: 'Toggle name for exact text match setting',
            }),
            onToggle: handleToggleIsExactTextMatch,
          },
        },
        {
          title: intl.formatMessage({
            id: 'EvaluateSettings.exactMatch',
            defaultMessage: 'Exact Match',
            description: 'Title for exact match explanation',
          }),
          description: intl.formatMessage({
            id: 'EvaluateSettings.exactMatchDescription',
            defaultMessage: 'Checks for capitalization and punctuation',
            description: 'Description for exact match functionality',
          }),
          type: 'status-texts' as const,
          props: {
            statusTexts: [
              {
                status: 'success' as const,
                text: 'ABC Inc',
              },
              {
                status: 'danger' as const,
                text: 'abc inc',
              },
            ],
          },
        },
        {
          title: intl.formatMessage({
            id: 'EvaluateSettings.meaningMatch',
            defaultMessage: 'Meaning Match',
            description: 'Title for meaning match explanation',
          }),
          description: intl.formatMessage({
            id: 'EvaluateSettings.meaningMatchDescription',
            defaultMessage: 'Checks for meaning',
            description: 'Description for meaning match functionality',
          }),
          type: 'status-texts' as const,
          props: {
            statusTexts: [
              {
                status: 'success' as const,
                text: intl.formatMessage({
                  id: 'EvaluateSettings.meaningMatchExample1',
                  defaultMessage: 'This is an NDA.',
                  description:
                    'Example text for meaning match - NDA abbreviation',
                }),
              },
              {
                status: 'success' as const,
                text: intl.formatMessage({
                  id: 'EvaluateSettings.meaningMatchExample2',
                  defaultMessage: 'This is an Non-Disclosure Agreement ',
                  description: 'Example text for meaning match - full NDA term',
                }),
              },
            ],
          },
        },
      ],
    },
  ];

  return (
    <Layout direction="column" spacing={2}>
      <Text preset="help">
        {intl.formatMessage({
          id: 'EvaluateSettings.evaluationSettings',
          defaultMessage: 'Evaluation Settings',
          description: 'Header text for evaluation settings section',
        })}
      </Text>
      <Info sections={sections} />
    </Layout>
  );
};
